import * as React from 'react'
import * as SheetPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * A panel docked to the right edge, over a dimmed page: about 480px wide, the
 * whole screen under 640px.
 *
 * Built on the same Radix dialog as `Dialog`, so it traps focus, closes on
 * Escape and on a click outside, and hands focus back to whatever opened it.
 * The difference is shape: a dialog is a decision the page waits on, a sheet is
 * a closer look at one thing in the page behind it, so the page stays visible
 * (and its URL state intact) while the sheet is open.
 *
 * Every sheet needs a `SheetTitle`; Radix warns without one, and it is what a
 * screen reader announces. Use `className="sr-only"` on it to hide it visually.
 */
const Sheet = SheetPrimitive.Root

const SheetTrigger = SheetPrimitive.Trigger

const SheetClose = SheetPrimitive.Close

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <SheetPrimitive.Portal>
    <SheetOverlay />
    <SheetPrimitive.Content
      ref={ref}
      className={cn(
        'fixed inset-y-0 right-0 z-50 flex h-full w-full flex-col border-l border-border bg-surface shadow-lg duration-200 sm:max-w-[480px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
        className
      )}
      {...props}
    >
      {children}
      <SheetPrimitive.Close className="absolute right-4 top-4 rounded-sm text-textMuted opacity-70 ring-offset-bg transition-opacity hover:bg-surface2 hover:text-text hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-mint focus:ring-offset-2 disabled:pointer-events-none">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </SheetPrimitive.Close>
    </SheetPrimitive.Content>
  </SheetPrimitive.Portal>
))
SheetContent.displayName = SheetPrimitive.Content.displayName

const SheetKickerContext = React.createContext<string | null>(null)

/**
 * Names, above every sheet header inside it, what the sheet belongs to: the
 * property, where a page shows many (the portfolio calendar). A sheet opened
 * on the property's own page has no need of it and is drawn without it.
 */
function SheetKicker({ value, children }: { value: string | null; children: React.ReactNode }) {
  return <SheetKickerContext.Provider value={value}>{children}</SheetKickerContext.Provider>
}

/** Fixed top strip: the title, and room on the right for the close button. */
const SheetHeader = ({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const kicker = React.useContext(SheetKickerContext)
  return (
    <div
      className={cn(
        'flex shrink-0 flex-col gap-1.5 border-b border-border py-4 pl-6 pr-12',
        className
      )}
      {...props}
    >
      {kicker && (
        <p data-testid="sheet-kicker" className="truncate text-xs font-medium text-textMuted">
          {kicker}
        </p>
      )}
      {children}
    </div>
  )
}
SheetHeader.displayName = 'SheetHeader'

/** The scrolling region between header and footer. */
const SheetBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('min-h-0 flex-1 overflow-y-auto px-6 py-4', className)} {...props} />
)
SheetBody.displayName = 'SheetBody'

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-tight tracking-tight text-text', className)}
    {...props}
  />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn('text-sm text-textMuted', className)}
    {...props}
  />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetOverlay,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetTitle,
  SheetDescription,
  SheetKicker,
}
